import { useEffect, useMemo, useState } from 'react';
import { materialConfirmGate } from '@coffman-tm/shared';
import type {
  InboxBlock,
  SettingsEmployee,
  SettingsEquipment,
  SettingsMaterial,
} from '@coffman-tm/shared';
import { api, ApiCallError } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { formatMoneyCents } from '../../lib/format';
import { AddEmployeeModal } from '../AddEmployeeModal';
import { AddEquipmentModal } from '../AddEquipmentModal';
import { AddMaterialModal } from '../AddMaterialModal';
import { ConfirmModal } from '../ConfirmModal';
import { Dropdown } from '../design';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';

type Section = 'labor' | 'equipment' | 'material';

const REASON_PRESETS = [
  'Foreman called — missed on the PDF',
  'Late addition from the field',
  "Correction to the foreman's report",
  'Logged on the wrong day',
] as const;
const REASON_CUSTOM = '__custom__';
const REASON_OPTIONS = [
  { value: '', label: 'Select a reason…' },
  ...REASON_PRESETS.map((r) => ({ value: r, label: r })),
  { value: REASON_CUSTOM, label: 'Type your own…' },
];

export interface InboxAddLineModalProps {
  section: Section;
  dailyEntryId: string;
  /** Roster defaults to prefill the quick-create roster modal. */
  defaultStRateCents: number;
  defaultPerDiemEligible: boolean;
  onClose: () => void;
  /** Receives the rehydrated block from the add endpoint. */
  onAdded: (block: InboxBlock) => void | Promise<void>;
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px', fontSize: 13, border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4, background: 'var(--surface)', color: 'var(--ink)',
};

export function InboxAddLineModal({
  section, dailyEntryId, defaultStRateCents, defaultPerDiemEligible, onClose, onAdded,
}: InboxAddLineModalProps) {
  const { token } = useAuth();
  const [employees, setEmployees] = useState<SettingsEmployee[]>([]);
  const [equipment, setEquipment] = useState<SettingsEquipment[]>([]);
  const [materials, setMaterials] = useState<SettingsMaterial[]>([]);
  const [entityId, setEntityId] = useState(''); // employee_id / equipment_id / material_id
  const [amount, setAmount] = useState('');
  const [rateDollars, setRateDollars] = useState(() =>
    section === 'labor' ? (defaultStRateCents / 100).toFixed(2) : '',
  );
  const [reasonChoice, setReasonChoice] = useState(''); // '' | preset | REASON_CUSTOM
  const [reasonCustom, setReasonCustom] = useState('');
  const reasonText = (reasonChoice === REASON_CUSTOM ? reasonCustom : reasonChoice).trim();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [quickAdd, setQuickAdd] = useState(false); // nested roster-create modal open
  const [zeroRateConfirmOpen, setZeroRateConfirmOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    if (section === 'labor') void api.settings.employees(token).then(setEmployees).catch(() => {});
    else if (section === 'equipment') void api.settings.equipment(token).then(setEquipment).catch(() => {});
    else void api.settings.materials(token).then(setMaterials).catch(() => {});
  }, [token, section]);

  // Prefill rate from the selected roster entity.
  useEffect(() => {
    if (!entityId) return;
    if (section === 'labor') {
      const e = employees.find((x) => x.id === entityId);
      if (e) setRateDollars((e.st_rate_cents / 100).toFixed(2));
    } else if (section === 'equipment') {
      const e = equipment.find((x) => x.id === entityId);
      if (e) setRateDollars((e.hourly_rate_cents / 100).toFixed(2));
    } else {
      const m = materials.find((x) => x.id === entityId);
      if (m) setRateDollars((m.default_rate_cents / 100).toFixed(2));
    }
  }, [entityId, section, employees, equipment, materials]);

  // Mounted only while open, so the lock is unconditional — and critically it
  // is NOT tied to the keydown effect below. That effect must re-run whenever
  // `quickAdd` / `zeroRateConfirmOpen` change; when the scroll lock rode along
  // in those deps, opening a nested dialog re-captured 'hidden' as the
  // "previous" overflow and left the page permanently unscrollable.
  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy && !quickAdd && !zeroRateConfirmOpen) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, quickAdd, zeroRateConfirmOpen, onClose]);

  const amountNum = Number(amount);
  const rateNum = Number(rateDollars);
  // All three sections now pick a roster entity (employee / equipment /
  // material) rather than typing a free-text description — materials used to
  // be the exception; this brings it in line with labor + equipment.
  const entityValid = entityId !== '';
  // A blank material rate must read as "missing", not $0 — Number('') is 0, so
  // without this check a forgotten rate would silently bill a free line.
  const rateProvided = section !== 'material' || rateDollars.trim() !== '';
  const valid = entityValid && Number.isFinite(amountNum) && amountNum > 0
    && rateProvided && Number.isFinite(rateNum) && rateNum >= 0
    && reasonText.length <= 280 && !busy;

  const selectedMaterial = useMemo(
    () => (section === 'material' ? materials.find((m) => m.id === entityId) ?? null : null),
    [section, materials, entityId],
  );

  const title = section === 'material' ? 'Add a material line' : `Add ${section}`;
  const amountLabel =
    section === 'material'
      ? `Quantity${selectedMaterial ? ` (${selectedMaterial.unit})` : ''}`
      : 'Total hrs';

  const handleSave = async (zeroRateAcknowledged = false) => {
    if (!token || !valid) return;
    const rateCents = Math.round(rateNum * 100);
    if (
      section === 'material' &&
      materialConfirmGate({ quantity: amountNum, rateCents, zeroRateAcknowledged }) ===
        'confirm_zero_rate'
    ) {
      // Manual material lines are born confirmed — this modal is their only
      // human gate, so the explicit $0 acknowledgment happens here.
      setZeroRateConfirmOpen(true);
      return;
    }
    if (section === 'material' && !selectedMaterial) {
      setErr('Selected material is no longer available — pick another.');
      return;
    }
    setBusy(true); setErr(null);
    const reasonTrim = reasonText || undefined;
    try {
      let res;
      if (section === 'labor') {
        res = await api.inbox.addLabor(token, dailyEntryId, { employee_id: entityId, total_hours: amountNum, rate_cents: rateCents, reason: reasonTrim });
      } else if (section === 'equipment') {
        res = await api.inbox.addEquipment(token, dailyEntryId, { equipment_id: entityId, hours: amountNum, rate_cents: rateCents, reason: reasonTrim });
      } else {
        res = await api.inbox.addMaterial(token, dailyEntryId, {
          quoted_text: selectedMaterial!.name,
          material_id: selectedMaterial!.id,
          unit: selectedMaterial!.unit,
          quantity: amountNum,
          rate_cents: rateCents,
          reason: reasonTrim,
        });
      }
      await onAdded(res.block);
      onClose();
    } catch (e) {
      // `kind === 'http'` gate: only a real server response carries a code
      // worth showing. Timeouts / network drops have synthetic payloads and a
      // human-readable `message`, which the else-branch surfaces instead.
      if (e instanceof ApiCallError && e.kind === 'http' && e.payload && typeof e.payload === 'object' && 'error' in e.payload) {
        setErr(String((e.payload as { error?: unknown }).error));
      } else setErr(e instanceof Error ? e.message : 'Add failed');
    } finally { setBusy(false); }
  };

  const options = useMemo(() => {
    if (section === 'labor') return employees.map((e) => ({ id: e.id, label: `${e.name} (${e.classification})` }));
    if (section === 'equipment') return equipment.map((e) => ({ id: e.id, label: `${e.asset_id} — ${e.description}` }));
    return materials.map((m) => ({
      id: m.id,
      label: `${m.name} (${m.unit}) — ${formatMoneyCents(m.default_rate_cents, { precise: true })}/${m.unit}`,
    }));
  }, [section, employees, equipment, materials]);

  const entityOptions = useMemo(
    () => [{ value: '', label: 'Select…' }, ...options.map((o) => ({ value: o.id, label: o.label }))],
    [options],
  );

  const entityLabel = section === 'labor' ? 'Employee' : section === 'equipment' ? 'Equipment' : 'Material';

  return (
    <div className="pdf-modal-overlay" onClick={() => { if (!busy && !quickAdd && !zeroRateConfirmOpen) onClose(); }} role="dialog" aria-modal="true" aria-label={title}>
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="confirm-modal-head"><div className="confirm-modal-title">{title}</div></div>
        {/* Explicit rows (regression fix 2026-08-02): the old single wrapping
            flex row collapsed after the Dropdown conversion — the wrapper
            sizes differently from a native select, every item wrapped, and
            the form stacked into a ragged column. Row 1: picker + quick-add.
            Row 2: amount + rate, equal halves. Row 3: reason. */}
        <div className="confirm-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>{entityLabel}</span>
              <Dropdown style={{ width: '100%' }} options={entityOptions} value={entityId} onChange={setEntityId} disabled={busy} />
            </label>
            <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd(true)} disabled={busy} style={{ fontSize: 12, flex: 'none' }}>
              + New {entityLabel}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>{amountLabel}</span>
              <input style={{ ...inputStyle, width: '100%' }} type="number" min="0" step="0.25" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={busy} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>Rate ($)</span>
              <input style={{ ...inputStyle, width: '100%' }} type="number" min="0" step="0.01" value={rateDollars} onChange={(e) => setRateDollars(e.target.value)} disabled={busy} />
            </label>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>Reason (optional)</span>
            <Dropdown
              style={{ width: '100%' }}
              options={REASON_OPTIONS}
              value={reasonChoice}
              onChange={setReasonChoice}
              disabled={busy}
            />
            {reasonChoice === REASON_CUSTOM && (
              <input
                style={{ ...inputStyle, marginTop: 4 }}
                value={reasonCustom}
                onChange={(e) => setReasonCustom(e.target.value)}
                placeholder="Type your reason"
                disabled={busy}
                maxLength={280}
                autoFocus
              />
            )}
          </label>
          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5, width: '100%' }}>{err}</div>}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-cta" onClick={() => void handleSave()} disabled={!valid}>{busy ? 'Adding…' : 'Add line'}</button>
        </div>
      </div>

      {section === 'labor' && (
        <AddEmployeeModal
          open={quickAdd}
          initialHjId=""
          initialName=""
          defaultStRateCents={defaultStRateCents}
          defaultPerDiemEligible={defaultPerDiemEligible}
          onClose={() => setQuickAdd(false)}
          onAdded={async (emp) => {
            setQuickAdd(false);
            if (token) setEmployees(await api.settings.employees(token));
            setEntityId(emp.id);
          }}
        />
      )}
      {section === 'equipment' && (
        <AddEquipmentModal
          open={quickAdd}
          initialAssetId=""
          initialDescription=""
          onClose={() => setQuickAdd(false)}
          onAdded={async (eq) => {
            setQuickAdd(false);
            if (token) setEquipment(await api.settings.equipment(token));
            setEntityId(eq.id);
          }}
        />
      )}
      {section === 'material' && (
        <AddMaterialModal
          open={quickAdd}
          initialName=""
          initialUnit=""
          onClose={() => setQuickAdd(false)}
          onAdded={async (mat) => {
            setQuickAdd(false);
            if (token) setMaterials(await api.settings.materials(token));
            setEntityId(mat.id);
          }}
        />
      )}

      <ConfirmModal
        open={zeroRateConfirmOpen}
        title="Confirm $0 rate"
        body={<>Rate is $0 — this line will bill $0.00. Confirm anyway?</>}
        confirmLabel="Bill $0.00"
        busy={busy}
        onConfirm={() => {
          setZeroRateConfirmOpen(false);
          void handleSave(true);
        }}
        onCancel={() => setZeroRateConfirmOpen(false)}
      />
    </div>
  );
}
